import {
  SECURITY_LEVEL,
  DISPLAY_TYPE,
} from '@pperform/pp-constants/widgetConfigs';
import LoadingScreen from 'components/LoadingScreen';
import { LoginModal } from 'components/LoginModal';
import { useTranslation } from 'react-i18next';
import { useFeatureToggleContext } from 'FeatureConfig.context';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import MerchantTrackingWidget from 'components/MerchantTrackingWidget';
import NoInfoUpdateModal from './components/NoInfoUpdateModal';
import NoParcelsModal from './components/NoParcelsModal';
import ReLoginModal from './components/ReLoginModal';
import { useShipment } from './hooks/useShipment/useShipment';
import {
  isShownNoInfoUpdateInline,
  isShownNoInfoUpdatePopup,
} from './utils/parcelNoInfoUpdate.utils';

const App = (): JSX.Element => {
  const { t } = useTranslation();
  const featureToggles = useFeatureToggleContext();
  const globalConfig = useGlobalConfigContext();
  const configs = globalConfig?.config;
  const { securityLevelBox, merchantCustom, noInfoUpdate, loadingScreen } =
    configs;
  const isAllowSearchForm =
    securityLevelBox.securityLevel !== SECURITY_LEVEL.ENCRYPTED_IDS_ONLY;

  const { shipments, isLoading, hasError, updateShipment, reloadShipment } =
    useShipment();

  // ======================================================================
  // Widget Display Logic
  //
  // DISPLAY_TYPE Configuration:
  // - loadingScreen.displayType (FULL_VIEW, IN_DETAILED_VIEW): Controls loading display
  // - merchantCustom.noInfoDisplayType (FULL_VIEW, SEARCH_FORM_VIEW, IN_DETAILED_VIEW): Controls shipments not found display
  // - noInfoUpdate.display (FULL_VIEW, SEARCH_FORM_VIEW, IN_DETAILED_VIEW): Controls shipments no info updates display
  //
  // Note: FULL_VIEW and SEARCH_FORM_VIEW are handled in this component
  //       IN_DETAILED_VIEW is handled inside MerchantTrackingWidget itself
  //
  // Rendering Priority:
  // 1. LoginModal
  //    Conditions: isAllowSearchForm + any of:
  //      a. initial state with no shipments
  //      b. isLoading + merchantCustom.noInfoDisplayType OR noInfoUpdate.display = SEARCH_FORM_VIEW
  //      c. hasError + merchantCustom.noInfoDisplayType = SEARCH_FORM_VIEW
  //      d. isShownNoInfoUpdateInline()
  //
  // 2. LoadingScreen
  //    Conditions: isLoading + loadingScreen.displayType = FULL_VIEW
  //    Note: Only reached if LoginModal wasn't shown (i.e., not in SEARCH_FORM_VIEW) to prevent UX flashing
  //
  // 3. ReLoginModal/NoParcelsModal
  //    Conditions: hasError + merchantCustom.noInfoDisplayType = FULL_VIEW
  //
  // 4. NoInfoUpdateModal
  //    Conditions: isShownNoInfoUpdatePopup()
  //
  // 5. MerchantTrackingWidget
  //    Default fallback: shipments display or IN_DETAILED_VIEW display
  // ======================================================================

  // 1. LoginModal
  const isInitialWithSearchForm =
    shipments.length === 0 && !isLoading && !hasError;

  const isLoadingWithSearchForm =
    isLoading &&
    (merchantCustom.noInfoDisplayType === DISPLAY_TYPE.SEARCH_FORM_VIEW ||
      noInfoUpdate.display === DISPLAY_TYPE.SEARCH_FORM_VIEW);

  const isErrorWithSearchForm =
    hasError &&
    merchantCustom.noInfoDisplayType === DISPLAY_TYPE.SEARCH_FORM_VIEW;

  const isNoInfoUpdateWithSearchForm = isShownNoInfoUpdateInline(
    shipments,
    globalConfig,
    featureToggles,
  );

  const shouldShowLoginModal =
    isInitialWithSearchForm ||
    isLoadingWithSearchForm ||
    isErrorWithSearchForm ||
    isNoInfoUpdateWithSearchForm;

  if (isAllowSearchForm && shouldShowLoginModal) {
    return (
      <LoginModal
        isLoading={isLoading}
        hasError={isErrorWithSearchForm}
        hasNoInfoUpdate={isNoInfoUpdateWithSearchForm}
      />
    );
  }

  // 2. LoadingScreen
  if (isLoading && loadingScreen.displayType === DISPLAY_TYPE.FULL_VIEW) {
    return <LoadingScreen />;
  }

  // 3. ReLoginModal/NoParcelsModal
  if (hasError && merchantCustom.noInfoDisplayType === DISPLAY_TYPE.FULL_VIEW) {
    if (isAllowSearchForm) {
      return <ReLoginModal globalConfig={globalConfig} />;
    }

    return (
      <NoParcelsModal
        message={
          merchantCustom.noInfoDisplayMessage || t('NO_INFORMATION_MESSAGE_1')
        }
      />
    );
  }

  // 4. NoInfoUpdateModal
  if (isShownNoInfoUpdatePopup(shipments, globalConfig, featureToggles)) {
    return <NoInfoUpdateModal globalConfig={globalConfig} />;
  }

  // 5. MerchantTrackingWidget
  return (
    <MerchantTrackingWidget
      data={shipments}
      isLoading={isLoading}
      updateShipment={updateShipment}
      reloadShipment={reloadShipment}
    />
  );
};

export default App;
